/* -------------------------------------------------------------------------------------------------
Restricted - Copyright (C) Siemens Healthcare GmbH/Siemens Medical Solutions USA, Inc., 2021. All rights reserved
------------------------------------------------------------------------------------------------- */
import { css, html, LitElement } from 'lit-element';
import { sharedStyles } from '../../styles';
import { dispatchAttributeChangedEvent } from '../../utils/attribute-changed-event-dispatcher.js';
import { dispatchCustomEvent } from '../../utils/custom-event-dispatcher.js';
import { decimalSeparator } from '../../utils/decimal-separator.js';
import { deviceIdentifier } from '../../utils/device-identifier.js';
import { FocusBlurMixin } from '../../utils/focus-blur-mixin.js';
import { KEYCODE } from '../../utils/keycode.js';

/**
 * @prop {String} label - Defines the label of the input. If the value is empty, it will be shown as a placeholder.
 * @prop {Boolean} visible - Indicates whether the keypad is open or closed. Can also set it to true / false to force open/close the keypad.
 * @prop {String} target - Defines the id of the control for which the keypad will be shown
 * @prop {Boolean} disabled - If set to true, the keypad buttons will be disabled.
 * @prop {Boolean} europeanNumberConvention - This property is auto-set or cleared initially when the component is loaded. If the browser's language follows European number conventions, then this property is auto-set to true. Else it is auto-cleared. This property can also be forced set to true to force it to follow European number conventions. Also, this property can be forced set to false to follow English number conventions. This would be particularly useful when used in systems like Android where changing the browser language is not so easy.
 * @prop {String} decimals - *hide
 * @prop {Boolean} condensed - *hide
 * @fires key-press - Fired when the keypad key is press(clicked/enter button pressed) on keypad key.
 * @fires closing - Fired when the keypad key first key and last key is tabbed.
 */

export class SHNumericKeypad extends FocusBlurMixin(LitElement) {
  static get properties() {
    return {
      visible: { type: Boolean, reflect: true },
      condensed: { type: Boolean, reflect: true },
      target: { type: String, reflect: true },
      decimals: { type: String, reflect: true },
      /** @ignore */
      disabled: { type: Boolean, reflect: true },
      /** @ignore */
      europeanNumberConvention: {
        type: Boolean,
        reflect: true,
        attribute: 'european-number-convention',
      },
      /** @ignore */
      _decimalSeparator: { type: String },
      /** @ignore */
      _isKeypadButtonFocused: { type: Boolean },
    };
  }

  static get styles() {
    return [
      sharedStyles,
      css`
        :host {
          visibility: hidden;
          opacity: 0;
          overflow: hidden;
          max-height: 0rem;
          height: 0rem;
          width: 13rem;
          z-index: 9999;
          position: fixed;
          background-color: var(--base-3);
          box-sizing: border-box;
          top: 2.5rem;
          box-shadow: var(--shadow-raised);
          transition: var(--transition-time) opacity var(--transition-time) ease-in-out;
        }
        :host([visible]) {
          visibility: visible;
          opacity: 1;
          overflow: visible;
          max-height: fit-content;
          height: fit-content;
          padding: 1rem;
          display: table;
        }
        :host([condensed]) {
          top: 2rem;
        }
        .functions-wrapper {
          position: relative;
          display: flex;
          width: 11rem;
          justify-content: space-between;
          padding-right: 0.5rem;
          box-sizing: border-box;
        }
        .functions-wrapper > ::slotted(*:first-child) {
          margin-left: 1rem;
          margin-right: 0.5rem;
        }
        .functions-wrapper > ::slotted(:not(*:first-child)) {
          margin-left: 0.5rem;
          margin-right: 0.5rem;
        }
        .keypad-main-frame {
          width: 11rem;
          height: 15rem;
          position: relative;
          display: flex;
          flex-wrap: wrap;
          justify-content: space-between;
          align-content: space-between;
        }
        .circle-buttons {
          transition: var(--transition-time) all ease-in-out;
          border-radius: 50%;
          width: 3rem;
          height: 3rem;
          display: flex;
          align-items: center;
          justify-content: center;
          background-color: rgba(var(--ui-1), var(--opacity-6));
          font: var(--header-2);
          color: var(--text-primary);
          cursor: pointer;
          user-select: none;
          -webkit-touch-callout: none;
          -ms-user-select: none;
          -moz-user-select: none;
          line-height: 1.5rem;
        }
        .erase-button {
          background-color: transparent;
        }
        :host(:not(.no-hovermq)) .circle-buttons:hover {
          background-color: rgba(var(--ui-1), var(--opacity-5));
        }
        .circle-buttons:active {
          background-color: rgba(var(--ui-1), var(--opacity-7));
        }
        :host([disabled]) .circle-buttons.dont-disable {
          pointer-events: all !important;
        }
        :host([disabled]) .circle-buttons:not(.dont-disable) {
          pointer-events: none;
          background-color: rgba(var(--ui-1), var(--opacity-7)) !important;
          color: rgba(var(--ui-1), var(--opacity-5));
        }
        :host([keypad][decimals='0']) .circle-buttons.decimals-point {
          pointer-events: none;
          opacity: 0;
        }
        .footer-wrapper {
          position: relative;
          display: flex;
          justify-content: flex-end;
        }
      `,
    ];
  }

  render() {
    return html`
      <div class="functions-wrapper">
        <slot name="functions" id="functionsSlot" @slotchange=${this.addMargins}></slot>
      </div>
      <div class="keypad-main-frame">
        <div
          value="1"
          class="circle-buttons"
          tabindex=${this.disabled ? -1 : 0}
          @click="${(e) => {
            this._circleButtonClick(e);
          }}"
          @keyup="${(e) => {
            this._EnterKeyIsPressedOnButton(e, e.target);
          }}"
          @keydown="${(e) => {
            this._keyDownOnNumberOne(e);
          }}"
        >
          1
        </div>

        <div
          value="2"
          class="circle-buttons"
          tabindex=${this.disabled ? -1 : 0}
          @click="${(e) => {
            this._circleButtonClick(e);
          }}"
          @keyup="${(e) => {
            this._EnterKeyIsPressedOnButton(e, e.target);
          }}"
        >
          2
        </div>

        <div
          value="3"
          class="circle-buttons"
          tabindex=${this.disabled ? -1 : 0}
          @click="${(e) => {
            this._circleButtonClick(e);
          }}"
          @keyup="${(e) => {
            this._EnterKeyIsPressedOnButton(e, e.target);
          }}"
        >
          3
        </div>

        <div
          value="4"
          class="circle-buttons"
          tabindex=${this.disabled ? -1 : 0}
          @click="${(e) => {
            this._circleButtonClick(e);
          }}"
          @keyup="${(e) => {
            this._EnterKeyIsPressedOnButton(e, e.target);
          }}"
        >
          4
        </div>

        <div
          value="5"
          class="circle-buttons"
          tabindex=${this.disabled ? -1 : 0}
          @click="${(e) => {
            this._circleButtonClick(e);
          }}"
          @keyup="${(e) => {
            this._EnterKeyIsPressedOnButton(e, e.target);
          }}"
        >
          5
        </div>

        <div
          value="6"
          class="circle-buttons"
          tabindex=${this.disabled ? -1 : 0}
          @click="${(e) => {
            this._circleButtonClick(e);
          }}"
          @keyup="${(e) => {
            this._EnterKeyIsPressedOnButton(e, e.target);
          }}"
        >
          6
        </div>

        <div
          value="7"
          class="circle-buttons"
          tabindex=${this.disabled ? -1 : 0}
          @click="${(e) => {
            this._circleButtonClick(e);
          }}"
          @keyup="${(e) => {
            this._EnterKeyIsPressedOnButton(e, e.target);
          }}"
        >
          7
        </div>

        <div
          value="8"
          class="circle-buttons"
          tabindex=${this.disabled ? -1 : 0}
          @click="${(e) => {
            this._circleButtonClick(e);
          }}"
          @keyup="${(e) => {
            this._EnterKeyIsPressedOnButton(e, e.target);
          }}"
        >
          8
        </div>

        <div
          value="9"
          class="circle-buttons"
          tabindex=${this.disabled ? -1 : 0}
          @click="${(e) => {
            this._circleButtonClick(e);
          }}"
          @keyup="${(e) => {
            this._EnterKeyIsPressedOnButton(e, e.target);
          }}"
        >
          9
        </div>

        <div
          class="circle-buttons decimals-point dont-disable"
          tabindex="0"
          @click="${(e) => {
            this._circleButtonClick(e);
          }}"
          @keyup="${(e) => {
            this._EnterKeyIsPressedOnButton(e, e.target);
          }}"
          value="${this._decimalSeparator}"
        >
          ${this._decimalSeparator}
        </div>

        <div
          value="0"
          class="circle-buttons"
          tabindex=${this.disabled ? -1 : 0}
          @click="${(e) => {
            this._circleButtonClick(e);
          }}"
          @keyup="${(e) => {
            this._EnterKeyIsPressedOnButton(e, e.target);
          }}"
        >
          0
        </div>

        <div
          class="circle-buttons erase-button dont-disable"
          tabindex="0"
          @click="${(e) => {
            this._circleButtonClick(e);
          }}"
          @keyup="${(e) => {
            this._EnterKeyIsPressedOnButton(e, e.target);
          }}"
          @keydown="${(e) => {
            this._keyDownOnEraseButton(e);
          }}"
        >
          <sh-icon icon="delete-number" tabindex="-1"></sh-icon>
        </div>
      </div>
      <sh-divider class="divider" spacing="m"></sh-divider>
      <div class="footer-wrapper">
        <slot
          name="footer"
          id="footer"
          @slotchange=${this._hideOrDisplayDividerBasedOnFooter}
        ></slot>
      </div>
    `;
  }

  constructor() {
    super();
    this._decimalSeparator = '.';
    this._isKeypadButtonFocused = false;
  }

  connectedCallback() {
    super.connectedCallback();
    if (deviceIdentifier.isTouchDevice()) {
      this.classList.add('no-hovermq');
    }
  }

  firstUpdated() {
    super.firstUpdated();
  }

  updated(changedProperties) {
    super.updated(changedProperties);
    if (changedProperties.has('europeanNumberConvention')) {
      this._europeanNumberConventionObserver();
    }
    const listOfProperties = [
      'visible',
      'condensed',
      'target',
      'decimals',
      'disabled',
      'europeanNumberConvention',
    ];
    dispatchAttributeChangedEvent(this, changedProperties, listOfProperties);
  }

  _circleButtonClick(e) {
    const shIconTag = 'SH-ICON';
    const isEraseButton =
      e.target.tagName === shIconTag || e.target.classList.contains('erase-button');
    const detail = {
      isEraseButton: isEraseButton,
      keyValueString: isEraseButton ? '' : e.target.getAttribute('value'),
      changeFocus: !this._isKeypadButtonFocused,
    };
    dispatchCustomEvent(this, 'key-press', detail);
    this._isKeypadButtonFocused = false;
  }

  _keyDownOnEraseButton(e) {
    if (e.keyCode === KEYCODE.TAB && !e.shiftKey) {
      const checkfooterNodes = true;
      if (!this._slotNodesPresent(checkfooterNodes)) {
        dispatchCustomEvent(this, 'closing', null);
      }
    }
  }

  _keyDownOnNumberOne(e) {
    if (e.keyCode === KEYCODE.TAB && e.shiftKey) {
      const checkfooterNodes = false;
      if (!this._slotNodesPresent(checkfooterNodes)) {
        dispatchCustomEvent(this, 'closing', null);
      }
    }
  }

  _slotNodesPresent(checkfooterNodes) {
    if (checkfooterNodes) {
      const footerSlot = this.shadowRoot.querySelector('[name="footer"]');
      if (footerSlot !== null) {
        const footerNodes = footerSlot.assignedNodes({
          flatten: true,
        });
        if (footerNodes.length !== 0) {
          return true;
        }
      }
    } else {
      const functionSlot = this.shadowRoot.querySelector('[name="functions"]');
      if (functionSlot !== null) {
        const functionNodes = functionSlot.assignedNodes({
          flatten: true,
        });
        if (functionNodes.length !== 0) {
          return true;
        }
      }
    }
    return false;
  }

  _EnterKeyIsPressedOnButton(e, element) {
    const shIconTag = 'SH-ICON';
    if (
      e.srcElement.tagName !== shIconTag &&
      ((e.keyCode === KEYCODE.ENTER && !this.disabled) ||
        (e.keyCode === KEYCODE.ENTER && element.classList.contains('dont-disable')))
    ) {
      element.focus();
      this._isKeypadButtonFocused = true;
      element.click();
    }
  }

  _hideOrDisplayDividerBasedOnFooter() {
    const footerNodes = this.shadowRoot.querySelector('#footer').assignedNodes({
      flatten: true,
    });
    if (footerNodes.length !== 0) {
      this.shadowRoot.querySelector('.divider').style.display = 'initial';
    } else {
      this.shadowRoot.querySelector('.divider').style.display = 'none';
    }
  }

  addMargins() {
    const functionsWrapper = this.shadowRoot.querySelector('.functions-wrapper');
    const functionNodes = this.shadowRoot.querySelector('#functionsSlot').assignedNodes({
      flatten: true,
    });
    functionsWrapper.style.marginBottom = functionNodes.length !== 0 ? '1rem' : '0rem';
  }

  _europeanNumberConventionObserver() {
    if (typeof this.europeanNumberConvention !== 'boolean') {
      this._decimalSeparator = decimalSeparator;
    } else {
      this._decimalSeparator = this.europeanNumberConvention ? ',' : '.';
    }
  }
}
customElements.define('sh-numeric-keypad', SHNumericKeypad);
